import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useParams } from "react-router-dom";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/layouts/PageHeader";
import { LucideArrowLeft, LucideUser, LucideEdit, LucidePencil, LucideEye, LucideChevronLeft, LucideChevronRight, LucideUsers } from "lucide-react";

const REPORTS_PAGE_SIZE = 10;

export default function SharedClass() {
  const { sharedClassId } = useParams();
  const { getToken } = useAuth();

  const [classData, setClassData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [reportsPage, setReportsPage] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const fetchSharedClass = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/shared-classes/${sharedClassId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch shared class");
        const data = await res.json();
        setClassData(data);
        setReports(data.reports || []);
        setFetchError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching shared class:", err);
        setFetchError("Could not load this shared class. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    fetchSharedClass();
    return () => controller.abort();
  }, [sharedClassId, getToken]);

  if (loading) return <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6 text-muted-foreground">Loading...</div>;
  if (fetchError) return <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6"><div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fetchError}</div></div>;
  if (!classData) return <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6 text-muted-foreground">Shared class not found.</div>;

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <Link
        to="/shared-classes"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <LucideChevronLeft size={14} />
        Shared Classes
      </Link>

      <div className="mb-5 flex items-center gap-2.5 rounded-md border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] px-3 py-2 text-sm text-[hsl(var(--warning-fg))]">
        <LucideUsers size={15} className="shrink-0" />
        <span>
          Shared with you{classData.sharedBy?.name ? <> by <strong>{classData.sharedBy.name}</strong></> : ""}
        </span>
        <Badge variant={classData.permission === "edit" ? "info" : "outline"}>
          {classData.permission === "edit" ? <LucidePencil size={10} /> : <LucideEye size={10} />}
          {classData.permission === "edit" ? "Can edit" : "View only — editing is disabled"}
        </Badge>
      </div>

      <PageHeader
        title={classData.name}
        subtitle={
          (classData.grade || classData.schoolYear)
            ? [classData.grade && `Grade ${classData.grade}`, classData.schoolYear].filter(Boolean).join(" · ")
            : undefined
        }
      />

      {reports.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <LucideUser size={40} className="mx-auto mb-3 opacity-40" />
          <p>No report cards in this class yet.</p>
        </div>
      )}

      <div className="space-y-2">
        {reports.slice(reportsPage * REPORTS_PAGE_SIZE, (reportsPage + 1) * REPORTS_PAGE_SIZE).map((card) => (
          <div key={card._id} className="border border-border rounded-lg p-4 bg-card flex items-center justify-between">
            <Link
              to={`/shared-classes/${sharedClassId}/reports/${card._id}`}
              state={{ permission: classData.permission }}
              className="flex-1"
            >
              <Button variant="ghost" className="text-foreground">
                <LucideUser className="mr-2" size={16} />
                {card.studentName}
              </Button>
            </Link>
            <div className="flex gap-2">
              {classData.permission === "edit" && (
                <Link
                  to={`/shared-classes/${sharedClassId}/reports/${card._id}`}
                  state={{ permission: classData.permission }}
                >
                  <Button size="sm">
                    <LucideEdit className="mr-1" size={14} />
                    Edit
                  </Button>
                </Link>
              )}
            </div>
          </div>
        ))}
        {reports.length > REPORTS_PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportsPage((p) => p - 1)}
              disabled={reportsPage === 0}
              className="gap-1"
            >
              <LucideChevronLeft size={14} />
              Prev
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {reportsPage + 1} of {Math.ceil(reports.length / REPORTS_PAGE_SIZE)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportsPage((p) => p + 1)}
              disabled={reportsPage === Math.ceil(reports.length / REPORTS_PAGE_SIZE) - 1}
              className="gap-1"
            >
              Next
              <LucideChevronRight size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
